import type { Components, CSSObject, Theme } from '@mui/material'
import {
  formLabelClasses,
  inputAdornmentClasses,
  inputBaseClasses,
  inputLabelClasses,
  outlinedInputClasses,
  selectClasses,
  svgIconClasses,
} from '@mui/material'

declare module '@mui/material/TextField' {
  export interface TextFieldPropsSizeOverrides {
    tiny: true
    large: true
  }
}
declare module '@mui/material/FormControl' {
  interface FormControlPropsSizeOverrides {
    tiny: true
    large: true
  }
}
declare module '@mui/material/InputBase' {
  interface InputBasePropsSizeOverrides {
    tiny: true
    large: true
  }
}

export const MuiInputBase = (theme: Theme): Components['MuiInputBase'] => ({
  styleOverrides: {
    root: {
      [`&.${inputBaseClasses.multiline}`]: { padding: theme.spacing(0.5, 0) },
      '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active':
        {
          caretColor: theme.palette.text.primary,
          transition: 'background-color 5000s ease-in-out 0s',
        },
    },
  },
})

export const MuiTextField = (theme: Theme): Components['MuiTextField'] => ({
  defaultProps: {
    margin: 'normal',
    fullWidth: true,
  },
  styleOverrides: {
    root: {
      textAlign: 'start',
      margin: 0,
    },
  },
  variants: [
    {
      props: { size: 'tiny' },
      style: {
        [`.${inputLabelClasses.root}`]: {
          ...theme.typography.CaptionMedium,
        },
      },
    },
  ],
})

export const MuiFormLabel: Components['MuiFormLabel'] = {
  styleOverrides: {
    asterisk: {
      [`&.${formLabelClasses.error}`]: {
        color: 'inherit',
      },
    },
  },
}

export const MuiInputLabel = (theme: Theme): Components['MuiInputLabel'] => ({
  styleOverrides: {
    root: {
      [theme.breakpoints.down('sm')]: {
        fontSize: '14px !important',
      },
      [theme.breakpoints.between('sm', 'md')]: {
        fontSize: '15px !important',
      },
      [theme.breakpoints.up('md')]: {
        fontSize: '16px !important',
      },
      fontWeight: '500 !important',

      [`&.${inputLabelClasses.disabled}`]: {
        color: theme.palette.text2,
      },
      [`&.${inputLabelClasses.focused}`]: {
        color: theme.palette.text2,
      },
      [`&.${inputLabelClasses.error}`]: {
        color: theme.palette.text2,
      },
    },
    outlined: {
      transform: 'translate(14px, 6px) scale(1)',
      [`&.${inputLabelClasses.shrink}`]: {
        transform: 'translate(14px, -12px) scale(0.75)',
      },
      [`&.${inputLabelClasses.sizeSmall}`]: {
        transform: 'translate(14px, 4px) scale(1)',
        [`&.${inputLabelClasses.shrink}`]: {
          transform: 'translate(14px, -10px) scale(0.75)',
        },
      },
    },
    asterisk: {
      marginLeft: theme.spacing(0.5),
      color: 'inherit',
    },
    sizeSmall: {
      [theme.breakpoints.down('sm')]: {
        ...(theme.typography.CaptionSmallMedium as CSSObject),
      },
      [theme.breakpoints.up('sm')]: {
        ...(theme.typography.CaptionMedium as CSSObject),
      },
    },
  },
})

export const MuiOutlinedInput = (
  theme: Theme,
): Components['MuiOutlinedInput'] => ({
  styleOverrides: {
    root: {
      ...theme.typography.BodyLargeRegular,
      borderRadius: +theme.shape.borderRadius * 2,
      '&:hover': {
        [`.${outlinedInputClasses.notchedOutline}`]: {
          borderColor: theme.palette.stroke4,
        },
      },
      [`&.${outlinedInputClasses.focused}`]: {
        [`.${outlinedInputClasses.notchedOutline}`]: {
          borderColor: '#35BFFF',
          borderWidth: 1,
        },
      },
      [`&.${outlinedInputClasses.disabled}`]: {
        backgroundColor: theme.palette.bg3,
        [`.${outlinedInputClasses.notchedOutline}`]: {
          borderColor: theme.palette.stroke2,
        },
        [`.${inputAdornmentClasses.root}`]: {
          color: theme.palette.text3,
        },
      },
      [`&.${outlinedInputClasses.error}`]: {
        '&:hover': {
          [`.${outlinedInputClasses.notchedOutline}`]: {
            borderColor: theme.palette.criticalStroke,
          },
        },
        [`&.${outlinedInputClasses.focused}`]: {
          [`.${outlinedInputClasses.notchedOutline}`]: {
            borderColor: theme.palette.criticalStroke,
            boxShadow: `0px 0px 0px 1px ${theme.palette.criticalStroke}`,
          },
        },
      },
      '&.MuiInputBase-formControl.MuiInputBase-adornedEnd': {
        padding: 0,
      },
    },
    notchedOutline: {
      borderColor: theme.palette.stroke4,
    },
    input: {
      height: 'auto',
      padding: theme.spacing(0.75, 1),
      textAlign: 'inherit',
      '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active':
        {
          WebkitTextFillColor: `${theme.palette.text.primary} !important`,
          caretColor: theme.palette.text.primary,
          transition: 'background-color 5000s ease-in-out 0s',
        },
      '&::placeholder': {
        color: theme.palette.text1,
        [theme.breakpoints.down('sm')]: {
          fontSize: '14px !important',
        },
        [theme.breakpoints.between('sm', 'md')]: {
          fontSize: '15px !important',
        },
        [theme.breakpoints.up('md')]: {
          fontSize: '16px !important',
        },
        fontWeight: 500,
      },
    },
    adornedStart: {
      paddingLeft: 0,
    },
    adornedEnd: {
      paddingRight: 0,
    },
  },
  variants: [
    {
      props: { size: 'tiny' },
      style: {
        ...theme.typography.CaptionRegular,
        input: {
          padding: theme.spacing(0.25, 0.75),
        },
        [`&.${inputBaseClasses.adornedStart}`]: {
          paddingLeft: 0,
        },
        [`&.${inputBaseClasses.adornedEnd}`]: {
          paddingRight: 0,
        },
        [`.${selectClasses.select}`]: {
          padding: theme.spacing(0.25, 0.75),
          paddingRight: `${theme.spacing(3)} !important`,
        },
        [`.${selectClasses.icon}`]: {
          fontSize: 20,
          right: 2,
        },
      },
    },
    {
      props: { size: 'small' },
      style: {
        ...theme.typography.BodyRegular,
        input: {
          padding: theme.spacing(0.5, 0.75),
        },
        [`&.${inputBaseClasses.adornedStart}`]: {
          paddingLeft: 0,
        },
        [`&.${inputBaseClasses.adornedEnd}`]: {
          paddingRight: 0,
        },
        [`.${selectClasses.select}`]: {
          padding: theme.spacing(0.5, 0.75),
          paddingRight: `${theme.spacing(4)} !important`,
        },
        [`.${selectClasses.icon}`]: {
          fontSize: 20,
          right: 6,
        },
      },
    },
    {
      props: { size: 'medium' },
      style: {
        [`.${selectClasses.select}`]: {
          padding: theme.spacing(0.75, 1),
          paddingRight: `${theme.spacing(5)} !important`,
        },
        [`.${selectClasses.icon}`]: {
          fontSize: 24,
          right: 8,
        },
      },
    },
    {
      props: { size: 'large' },
      style: {
        input: {
          padding: theme.spacing(1.25, 1.5),
        },
        [`&.${inputBaseClasses.adornedStart}`]: {
          paddingLeft: 0,
        },
        [`&.${inputBaseClasses.adornedEnd}`]: {
          paddingRight: 0,
        },
        [`.${selectClasses.select}`]: {
          padding: theme.spacing(1.25, 1.5),
          paddingRight: `${theme.spacing(6)} !important`,
        },
        [`.${selectClasses.icon}`]: {
          fontSize: 24,
          right: 12,
        },
      },
    },
  ],
})

export const MuiInputAdornment = (
  theme: Theme,
): Components['MuiInputAdornment'] => ({
  styleOverrides: {
    root: {
      color: theme.palette.text2,
      [`&.${inputAdornmentClasses.positionStart}`]: {
        marginRight: 0,

        paddingLeft: theme.spacing(1.5),
      },
      [`&.${inputAdornmentClasses.positionEnd}`]: {
        marginLeft: 0,

        paddingRight: theme.spacing(1.5),
      },
      ['&.MuiInputAdornment-sizeTiny, &.MuiInputAdornment-sizeSmall']: {
        [`.${svgIconClasses.root}`]: {
          fontSize: 20,
        },
      },
      '& .MuiTypography-root': {
        lineHeight: 1,
      },
    },
  },
})

export const MuiFormHelperText = (
  theme: Theme,
): Components['MuiFormHelperText'] => ({
  styleOverrides: {
    root: {
      ...theme.typography.CaptionRegular,
      marginTop: 8,
      marginLeft: 0,
      marginRight: 'var(--spacing__SCF_spacingS) !important',
      textAlign: 'right',
    },
  },
  variants: [
    {
      props: { margin: 'dense' },
      style: {
        marginTop: 0,
        marginBottom: 0,
      },
    },
  ],
})
